// src/AppContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import useAuth from './hooks/useAuth';

const API_BASE_URL = window.location.hostname === 'localhost' ? 'http://localhost:7071' : '';

const AppContext = createContext(null);

export function AppProvider({ children }) {
    const { user, isLoading: isAuthLoading } = useAuth();
    const [appState, setAppState] = useState('loading');
    const [customerConfig, setCustomerConfig] = useState(null);
    const [error, setError] = useState(null);

    const fetchUserStatus = useCallback(async () => {
        if (!user) return;
        
        // Don't set to loading on re-fetch to avoid screen flicker,
        // individual components can show their own loading state.
        // setAppState('loading'); 

        const maxRetries = 3;
        let attempt = 0;
        while (attempt < maxRetries) {
            attempt++;
            try {
                const response = await fetch(`${API_BASE_URL}/api/user/status`);
                if (response.ok) {
                    const data = await response.json();
                    setCustomerConfig(data);
                    setAppState('onboarded');
                    setError(null);
                    return;
                } else if (response.status === 404) {
                    setAppState('needs_onboarding');
                    setError(null);
                    return;
                }
                throw new Error(`API returned status ${response.status}`);
            } catch (err) {
                console.warn(`Attempt ${attempt} to check user status failed:`, err);
                if (attempt >= maxRetries) {
                    setAppState('error');
                    setError('Could not load your configuration. Please try refreshing the page.');
                    return;
                }
                await new Promise(resolve => setTimeout(resolve, 2000));
            }
        }
    }, [user]);

    useEffect(() => {
        if (isAuthLoading) {
            setAppState('loading');
            return;
        }
        if (!user) {
            setAppState('logged_out');
            return;
        }
        fetchUserStatus();
    }, [user, isAuthLoading, fetchUserStatus]);

    const value = {
        user,
        customerConfig,
        appState,
        error,
        reloadConfig: fetchUserStatus
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useAppContext = () => useContext(AppContext);
